"use client"

import { useId, useRef, useState } from "react"
import { MeetingParticipant, MeetingShare } from "@/schema"
import { useGlobalDialogStore } from "@/stores/use-global-dialog-store"
import { Button, Checkbox, Input } from "@ui/shad"
import { format, isToday } from "date-fns"
import { Check, Copy, Eye, Mail, Plus, Share2, Trash2 } from "lucide-react"
import { toast } from "sonner"

import { useCreateMeetingShare, useMeetingShares, useRevokeMeetingShare } from "@/hooks/api/meeting-shares"
import { SimpleFormDialog } from "@/components/dialogs"
import { ComposeEmail } from "@/components/email/compose"

import { meetingShareRecipients } from "./meeting-share-recipients"

interface ShareMeetingDialogProps {
  meetingId: string
  participants?: MeetingParticipant[]
  internalParticipants?: MeetingParticipant[]
  meetingTitle?: string
  meetingStartTime?: string | Date
}

function buildShareDateLabel(meetingStartTime?: string | Date): string {
  if (!meetingStartTime) return "our recent meeting"

  const parsedDate = new Date(meetingStartTime)
  if (Number.isNaN(parsedDate.getTime())) return "our recent meeting"

  return isToday(parsedDate) ? "today" : `on ${format(parsedDate, "MMMM d, yyyy")}`
}

export default function ShareMeetingDialog({
  meetingId,
  meetingTitle,
  meetingStartTime,
  participants = [],
  internalParticipants = [],
}: ShareMeetingDialogProps) {
  const formId = useId()
  const [open, setOpen] = useState(false)
  const [customRecipients, setCustomRecipients] = useState<
    { id: string; email: string; firstName: string; lastName: string }[]
  >([])
  const [selectedEmails, setSelectedEmails] = useState<string[]>([])
  const [isSharing, setIsSharing] = useState(false)
  const [copiedToken, setCopiedToken] = useState<string | null>(null)
  const isOpeningEmailDialogRef = useRef(false)

  const { data: sharesData, isPending: isLoadingShares } = useMeetingShares(meetingId)
  const createShare = useCreateMeetingShare()
  const revokeShare = useRevokeMeetingShare()
  const openDialog = useGlobalDialogStore((s) => s.openDialog)

  const shares = sharesData?.filter((s) => !s.revoked_at) ?? []

  const sharedEmails = new Set(shares.map((share) => share.email.trim().toLowerCase()))
  const attendees = meetingShareRecipients(participants, internalParticipants).filter(
    (attendee) => !sharedEmails.has(attendee.email)
  )
  const selectedAttendees = attendees.filter((attendee) => selectedEmails.includes(attendee.email))
  const hasRecipients = selectedAttendees.length > 0 || customRecipients.some((recipient) => recipient.email.trim())

  function updateCustomRecipient(id: string, field: "email" | "firstName" | "lastName", value: string) {
    setCustomRecipients((current) =>
      current.map((recipient) => (recipient.id === id ? { ...recipient, [field]: value } : recipient))
    )
  }

  async function handleCreateShare() {
    if (!hasRecipients || isSharing || isLoadingShares) return
    const recipients = new Map(selectedAttendees.map((attendee) => [attendee.email, attendee]))
    for (const recipient of customRecipients) {
      const normalizedEmail = recipient.email.trim().toLowerCase()
      if (!normalizedEmail) return
      recipients.set(normalizedEmail, {
        email: normalizedEmail,
        first_name: recipient.firstName.trim() || recipients.get(normalizedEmail)?.first_name,
        last_name: recipient.lastName.trim() || recipients.get(normalizedEmail)?.last_name,
      })
    }

    setIsSharing(true)
    try {
      for (const recipient of recipients.values()) {
        await createShare.mutateAsync({ meetingId, ...recipient })
      }
      setCustomRecipients([])
      setSelectedEmails([])
      toast.success(`Meeting shared with ${recipients.size} ${recipients.size === 1 ? "person" : "people"}`)
    } catch {
      // The mutation cache displays the error. Keep the selection so a retry can reuse existing shares.
    } finally {
      setIsSharing(false)
    }
  }

  function handleCopyLink(share: MeetingShare) {
    navigator.clipboard.writeText(share.portal_url)
    setCopiedToken(share.token)
    toast.success("Link copied to clipboard")
    setTimeout(() => setCopiedToken(null), 2000)
  }

  function handleSendEmail(share: MeetingShare) {
    const subject = `Meeting recording: ${meetingTitle || "Meeting"}`
    const greetingName = share.first_name?.trim() || "there"
    const meetingDateText = buildShareDateLabel(meetingStartTime)
    const body = `<p>Hi ${greetingName},</p><br><p>I wanted to share the recording to our recent meeting ${meetingDateText}. Here is the link, you'll just verify your email to get access.</p><br><p><a href="${share.portal_url}" target="_blank" rel="noopener noreferrer">View meeting recording</a></p><br><p>Thanks!</p>`

    isOpeningEmailDialogRef.current = true
    setOpen(false)

    window.setTimeout(() => {
      openDialog({
        variant: "custom",
        content: (
          <ComposeEmail
            toEmails={[{ email: share.email, name: share.first_name || null, type: null }]}
            startingSubject={subject}
            startingHTML={body}
          />
        ),
      })
      window.setTimeout(() => {
        isOpeningEmailDialogRef.current = false
      }, 1000)
    }, 0)
  }

  function handleRevoke(share: MeetingShare) {
    revokeShare.mutate(
      { meetingId, shareId: share.id },
      {
        onSuccess: () => {
          const email = share.email.trim().toLowerCase()
          setSelectedEmails((current) => current.filter((selectedEmail) => selectedEmail !== email))
        },
      }
    )
  }

  return (
    <>
      <Button
        variant="outline"
        size="sm"
        onClick={() => {
          setSelectedEmails([])
          setOpen(true)
        }}
      >
        <Share2 />
        Share
      </Button>
      <SimpleFormDialog
        fixedHeight
        open={open}
        onOpenChange={(nextOpen) => {
          if (!isSharing) setOpen(nextOpen)
        }}
        title="Share meeting"
        onCloseAutoFocus={(event) => {
          if (isOpeningEmailDialogRef.current) {
            event.preventDefault()
            isOpeningEmailDialogRef.current = false
          }
        }}
        action={{ label: "Share", form: formId, disabled: !hasRecipients || isLoadingShares, pending: isSharing }}
      >
        <div className="space-y-2">
          <form
            id={formId}
            onSubmit={(event) => {
              event.preventDefault()
              void handleCreateShare()
            }}
            className="grid gap-2"
          >
            {!isLoadingShares && attendees.length > 0 && (
              <fieldset className="space-y-2" disabled={isSharing}>
                <legend className="mb-2 text-sm font-medium">Meeting attendees</legend>
                <div className="max-h-48 divide-y overflow-y-auto rounded-md border">
                  {attendees.map((attendee) => (
                    <label key={attendee.email} className="flex cursor-pointer items-center gap-2 px-3 py-2">
                      <Checkbox
                        checked={selectedEmails.includes(attendee.email)}
                        disabled={isSharing}
                        onCheckedChange={(checked) => {
                          setSelectedEmails((current) =>
                            checked ? [...current, attendee.email] : current.filter((email) => email !== attendee.email)
                          )
                        }}
                      />
                      <span className="min-w-0 text-sm">
                        <span className="block truncate font-medium">
                          {[attendee.first_name, attendee.last_name].filter(Boolean).join(" ") || attendee.email}
                        </span>
                        {(attendee.first_name || attendee.last_name) && (
                          <span className="text-muted-foreground block truncate text-xs">{attendee.email}</span>
                        )}
                      </span>
                    </label>
                  ))}
                </div>
              </fieldset>
            )}
            {customRecipients.map((recipient, index) => (
              <div key={recipient.id} className="grid gap-2 rounded-md border p-3">
                <div className="grid gap-2 sm:grid-cols-2">
                  <Input
                    autoFocus
                    aria-label={`Recipient ${index + 1} first name`}
                    placeholder="First name"
                    value={recipient.firstName}
                    onChange={(event) => updateCustomRecipient(recipient.id, "firstName", event.target.value)}
                    disabled={isSharing}
                  />
                  <Input
                    aria-label={`Recipient ${index + 1} last name`}
                    placeholder="Last name"
                    value={recipient.lastName}
                    onChange={(event) => updateCustomRecipient(recipient.id, "lastName", event.target.value)}
                    disabled={isSharing}
                  />
                </div>
                <div className="flex items-center gap-2">
                  <Input
                    aria-label={`Recipient ${index + 1} email address`}
                    type="email"
                    required
                    placeholder="Enter email address"
                    value={recipient.email}
                    onChange={(event) => updateCustomRecipient(recipient.id, "email", event.target.value)}
                    disabled={isSharing}
                  />
                  <Button
                    type="button"
                    variant="ghost"
                    size="iconSm"
                    aria-label={`Remove recipient ${index + 1}`}
                    disabled={isSharing}
                    onClick={() => setCustomRecipients((current) => current.filter((item) => item.id !== recipient.id))}
                  >
                    <Trash2 />
                  </Button>
                </div>
              </div>
            ))}
            <Button
              type="button"
              variant="ghost"
              size="sm"
              className="justify-self-start"
              disabled={isSharing}
              onClick={() =>
                setCustomRecipients((current) => [
                  ...current,
                  { id: crypto.randomUUID(), email: "", firstName: "", lastName: "" },
                ])
              }
            >
              <Plus />
              Add another recipient
            </Button>
          </form>

          {shares.length > 0 && (
            <div className="space-y-2">
              <p className="text-muted-foreground text-xs font-medium tracking-wide uppercase">Shared with</p>
              <div className="divide-y rounded-md border">
                {shares.map((share) => (
                  <div key={share.id} className="flex items-center justify-between gap-2 px-3 py-2">
                    <div className="min-w-0 flex-1">
                      <p className="truncate text-sm font-medium">
                        {share.first_name || share.last_name
                          ? `${share.first_name || ""} ${share.last_name || ""}`.trim()
                          : share.email}
                      </p>
                      <p className="text-muted-foreground truncate text-xs">{share.email}</p>
                      <div className="text-muted-foreground flex items-center gap-2 text-xs">
                        <span className="flex items-center gap-1">
                          <Eye className="size-3" />
                          {share.view_count} {share.view_count === 1 ? "view" : "views"}
                        </span>
                        {share.last_viewed_at && (
                          <span>Last viewed {format(new Date(share.last_viewed_at), "MMM d")}</span>
                        )}
                      </div>
                    </div>
                    <div className="flex items-center gap-1">
                      <Button
                        variant="ghost"
                        size="iconXs"
                        onClick={() => handleCopyLink(share)}
                        title="Copy link"
                        disabled={isSharing}
                      >
                        {copiedToken === share.token ? <Check className="text-green-accent" /> : <Copy />}
                      </Button>
                      <Button
                        variant="ghost"
                        size="iconXs"
                        onClick={() => handleSendEmail(share)}
                        title="Send via email"
                        disabled={isSharing}
                      >
                        <Mail />
                      </Button>
                      <Button
                        variant="ghost"
                        size="iconXs"
                        onClick={() => handleRevoke(share)}
                        title="Revoke access"
                        disabled={isSharing || revokeShare.isPending}
                      >
                        <Trash2 />
                      </Button>
                    </div>
                  </div>
                ))}
              </div>
            </div>
          )}
        </div>
      </SimpleFormDialog>
    </>
  )
}
